import React, { useState, useEffect } from 'react';

const Hero: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const heroImages = [
    '/assets/nfi0.jpeg',
    '/assets/nfi00.jpeg',
    '/assets/nfi000.jpeg',
    '/assets/nfi1.jpeg',
    '/assets/nfi2.jpeg',
    '/assets/nfi3.jpeg',
    '/assets/nfi4.jpeg',
    '/assets/nfi5.jpeg',
    '/assets/nfi6.jpeg',
    '/assets/nfi7.jpeg',
    '/assets/nfi8.jpeg',
    '/assets/nfi9.jpeg',
    '/assets/nfi11.jpeg',
    '/assets/nfi22.jpeg',
    '/assets/nfi44.jpeg',
    '/assets/nfi55.jpeg',
    '/assets/nfi111.jpeg',
    '/assets/nfi999.jpeg',
    '/assets/nfi1111.jpeg'
  ];

  const imageAlts = [
    'NFI humanitarian activity 0',
    'NFI humanitarian activity 00',
    'NFI humanitarian activity 000',
    'NFI humanitarian activity 1',
    'NFI humanitarian activity 2',
    'NFI humanitarian activity 3',
    'NFI humanitarian activity 4',
    'NFI humanitarian activity 5',
    'NFI humanitarian activity 6',
    'NFI humanitarian activity 7',
    'NFI humanitarian activity 8',
    'NFI humanitarian activity 9',
    'NFI humanitarian activity 11',
    'NFI humanitarian activity 22',
    'NFI humanitarian activity 44',
    'NFI humanitarian activity 55',
    'NFI humanitarian activity 111',
    'NFI humanitarian activity 999',
    'NFI humanitarian activity 1111'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
        setIsTransitioning(false);
      }, 500); // Fade out duration
      
    }, 4000); // Total time per image (4 seconds)

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-[rgb(138,201,9)]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230369a1' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                A society devoid of{' '}
                <span className="text-white">poverty and social deprivation</span>
              </h1>
              <p className="text-lg md:text-xl text-white leading-relaxed max-w-2xl">
                We mitigate the impact of poverty and social deprivation among vulnerable groups
                through provision of innovative and sustainable social empowerment services that
                enhance human rights and dignity.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#about"
                className="btn btn-primary text-base px-8 py-4"
              >
                Learn More
              </a>
              <a
                href="#contact"
                className="btn btn-outline text-base px-8 py-4"
              >
                Get in Touch
              </a>
            </div>
          </div>

          {/* Hero Image Marquee */}
          <div className="relative animate-slide-up">
            <div className="relative">
              <div className="relative w-full h-[500px] overflow-hidden rounded-2xl shadow-2xl">
                {heroImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={imageAlts[index]}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${
                      index === currentImageIndex && !isTransitioning
                        ? 'opacity-100 translate-x-0' 
                        : 'opacity-0 -translate-x-full'
                    }`}
                  />
                ))}
              </div>
              
              {/* Image indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsTransitioning(true);
                      setTimeout(() => {
                        setCurrentImageIndex(index);
                        setIsTransitioning(false);
                      }, 250);
                    }}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
            </div>

            {/* Floating cards */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-brand-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-black">Humanitarian Aid</p>
                  <p className="text-sm text-black">Supporting communities</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg p-4 border border-brand-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-black">Community Impact</p>
                  <p className="text-sm text-black">Empowering lives</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
